import {
	Vue, Component, Prop, Ref, toNative,
} from 'vue-facing-decorator';
import * as DB from 'interfaces/database';
import mitt from 'mitt';
import Swal from 'sweetalert2';
import { TabEvent } from 'interfaces/app';
import { httpClient } from 'utils/http';
import { CellComponentExtended, ColumnDefinitionExtended, TabulatorFull as Tabulator } from 'tabulator-tables';
import { createInstance } from 'utils/vue';
import TabulatorBtn from 'components/Tabulator/TabulatorBtn';
import CustomSelect from 'components/CustomSelect';
import Template from './template.vue';

const eventBus = mitt<TabEvent<DB.TemplatePositionStateModel>>();
@Component({
	components: {
		TabulatorBtn,
		CustomSelect,
	},
	emits: ['routeTemplateState'],
	mixins: [Template],
})
class TemplatePosition extends Vue {
	@Ref('templatePositionTable')
	private readonly tableReference!: HTMLDivElement;

	private isLoading = false;

	private columnDefs: ColumnDefinitionExtended[] = [];

	private showTemplateState = false;

	private layoutdata = {} as DB.LayoutModel;

	private TemplateStateData = '';

	private singleTemplateState = {} as DB.TemplatePositionPhotoModel;

	private serialnumber: undefined | number = undefined;

	private options: string[] = ['photo', 'text'];

	private table?: Tabulator;

	@Prop({
		type: String,
		required: true,
	})
	public readonly templateStateId!: string;

	private get loggedIn(): boolean {
		return this.$auth0.isAuthenticated.value;
	}

	protected beforeMount(): void {
		this.columnDefs = [
			{
				field: 'width',
				title: 'Width',
			},
			{
				title: 'Height',
				field: 'height',
			},
			{
				title: 'x-axis',
				field: 'x_axis',
			},
			{
				title: 'y-axis',
				field: 'y_axis',
			},
			{
				title: 'z-axis',
				field: 'z_axis',
			},
			{
				title: 'Angle',
				field: 'angle',
			},
			{
				title: 'Actions',
				formatter: (cell: CellComponentExtended<DB.TemplatePositionStateModel>) => {
					const instance = createInstance({
						component: TabulatorBtn,
						props: {
							data: cell.getData(),
							buttons: [
								{
									id: 'edit',
									eventName: 'editItem',
									className: 'fa-edit',
								},
								{
									id: 'delete',
									eventName: 'deleteItem',
									className: 'fa-times',
								},
							],
							eventBus,
						},
					});
					return instance;
				},
			},
		] as ColumnDefinitionExtended[];
	}

	protected mounted(): void {
		eventBus.on(
			'deleteItem',
			this.deleteTemplateEdit,
		);
		eventBus.on(
			'editItem',
			this.editTemplateEdit,
		);
		this.tableInitialization();
		this.table?.on(
			'tableBuilt',
			this.onTableBuilt,
		);
	}

	protected beforeUnmount(): void {
		eventBus.off(
			'deleteItem',
			this.deleteTemplateEdit,
		);
		eventBus.off(
			'editItem',
			this.editTemplateEdit,
		);
		this.table?.off(
			'tableBuilt',
			this.onTableBuilt,
		);
		this.table?.destroy();
	}

	private onTableBuilt(): void {
		this.getData();
	}

	private tableInitialization(): void {
		this.table = new Tabulator(
			this.tableReference,
			{
				height: '60vh',
				layout: 'fitColumns',
				columns: this.columnDefs,
			},
		);
	}

	private async getData(): Promise<void> {
		const parameter = new URLSearchParams({
			limit: '0',
		});
		this.table?.alert('Loading');
		try {
			const { data } = await httpClient.get<DB.TemplatePositionStateModel[]>(`/api/templatestate/${this.templateStateId}/positions?${parameter}`);
			this.table?.setData(data);
		} catch (err: any) {
			this.$toastError(err.message);
		} finally {
			this.table?.clearAlert();
		}
	}

	protected toggleTemplateState(): void {
		this.showTemplateState = !this.showTemplateState;
	}

	protected async saveTemplateState(): Promise<void> {
		this.isLoading = true;
		const url = this.TemplateStateData === 'text' ? 'templatepositiontext' : 'templatepositionphoto';
		try {
			const { data } = await httpClient.post(
				`/api/${url}`,
				{
					...this.singleTemplateState,
				},
			);
			this.table?.addData(data);
		} catch (error: any) {
			this.$toastError(error.message);
		} finally {
			this.isLoading = false;
		}
	}

	private editTemplateEdit(data: TabEvent<DB.TemplatePositionStateModel>['editItem']): void {
		this.$emit(
			'routeTemplateState',
			{
				id: data.id,
				data: data.params,
			},
		);
	}

	private deleteTemplateEdit(data: TabEvent<DB.TemplatePositionStateModel>['deleteItem']): void {
		Swal.fire({
			title: 'Are you sure?',
			text: 'You will not be able to recover this file!',
			icon: 'warning',
			showCancelButton: true,
			customClass: {
				confirmButton: 'btn btn-danger m-1',
				cancelButton: 'btn btn-secondary m-1',
			},
			confirmButtonText: 'Yes, delete it!',
			html: false,
		}).then((result) => {
			if (result.value) {
				this.table?.alert('Loading..');
				const url = data.params.type === 'photo' ? 'templatepositionphoto' : 'templatepositiontext';
				// eslint-disable-next-line promise/no-nesting
				return httpClient.delete(`/api/${url}/${data.id}`).then(() => {
					this.$toastSuccess('Item Deleted');
					this.table?.deleteRow(data.id);
					return null;
				}).finally(() => {
					this.table?.clearAlert();
				}).catch((err) => {
					this.$toastError(err.message);
				});
			}
			return undefined;
		}).catch((err) => {
			this.$toastError(err.message);
		});
	}
}

export default toNative(TemplatePosition);
